import { UserGameProfileInterface } from '@campus/dal';
import { Dictionary } from '@ngrx/entity';

export interface GameLearningEventsServiceInterface {
  registerEventListener(): void;
}

export enum GameMessageType {
  /////////////////////////////
  // RECEIVING FROM THE GAME //
  /////////////////////////////
  /** Received when the game reports it has loaded */
  GAME_LOADED = 'GAME_LOADED',
  /** Received when a playthrough of the game has completed */
  GAME_COMPLETED = 'GAME_COMPLETED',
  /** Received when the user requested to restart the game */
  GAME_RESTARTED = 'GAME_RESTARTED',
  /** Received when a single 'question' in the game was answered */
  GAME_EXERCISE = 'GAME_EXERCISE',
  /** Received when the game reports the window is being closed */
  GAME_WINDOW_CLOSED = 'GAME_WINDOW_CLOSED',
  /** Received when the user asks to view instructions inside the game */
  GAME_OPEN_INSTRUCTIONS = 'GAME_OPEN_INSTRUCTIONS',
  //** Received when the game needs Profile data */
  GAME_GET_PROFILE_DATA = 'GAME_GET_PROFILE_DATA',
  //** Received when the game wants to save Profile data */
  GAME_SAVE_PROFILE_DATA = 'GAME_SAVE_PROFILE_DATA',
  /** Received when the game wants to get the user's kwetons */
  GAME_GET_KWETONS = 'GAME_GET_KWETONS',
  /** Received when the game wants purchase an item */
  GAME_PURCHASE = 'GAME_PURCHASE',
  /** Received when the game earns a gift */
  GAME_EARNING = 'GAME_EARNING',

  ///////////////////////////
  //  SENDING TO THE GAME  //
  ///////////////////////////
  /** Sent when a registration was created so the game can keep track of it */
  GAME_REGISTRATION = 'GAME_REGISTRATION',
  /** Sent when the game window should open a specific URL (used after receiving GAME_OPEN_INSTRUCTIONS) */
  GAME_OPEN_URL = 'GAME_OPEN_URL',
  /** Sent when the profile data is received so the game can use it */
  GAME_PROFILE_DATA = 'GAME_PROFILE_DATA',
  /** Sent when the profile data is saved (with id) */
  GAME_PROFILE_DATA_SAVED = 'GAME_PROFILE_DATA_SAVED',
  /** Sent when the user kwetons are received */
  GAME_USER_KWETONS = 'GAME_USER_KWETONS',
  /** Sent when the user makes a successful purchase */
  GAME_USER_PURCHASED = 'GAME_USER_PURCHASED',
  /** Sent when the user fails to make a purchase */
  GAME_USER_PURCHASE_FAILED = 'GAME_USER_PURCHASE_FAILED',
  /** Sent when the user makes earns successfully */
  GAME_USER_EARNED = 'GAME_USER_EARNED',
  /** Sent when the user earning fails */
  GAME_USER_EARNING_FAILED = 'GAME_USER_EARNING_FAILED',
  /** Sent when the user completed a game */
  GAME_COMPLETED_PROCESSED = 'GAME_COMPLETED_PROCESSED',
}

export interface GameMessageBase {
  type: GameMessageType;
  data: any;
  sourceWindow?: Window;
  /** Validated origin the message arrived from; replies are targeted at it. */
  sourceOrigin?: string;
}

export interface GameLoadedMessage extends GameMessageBase {
  type: GameMessageType.GAME_LOADED;
  data: {
    gameName: string;
    eduContentId: number;
  };
}

export interface GameRegistrationMessage extends GameMessageBase {
  type: GameMessageType.GAME_REGISTRATION;
  data: {
    registrationId: number;
    eduContentId: number;
    currentHighscore: number;
    content?: string;
  };
}

export interface GameCompletedMessage extends GameMessageBase {
  type: GameMessageType.GAME_COMPLETED;
  data: {
    gameName: string;
    registrationId: number;
    eduContentId: number;
    score: number; // int
    playTime: number; // float (seconds)
    kwetonCount: number; // int
    totalExercises: number; // int
    correctExercises: number; // int
  };
}

export interface GameRestartedMessage extends GameMessageBase {
  type: GameMessageType.GAME_RESTARTED;
  data: {
    gameName: string;
    eduContentId: number;
  };
}

export interface GameExerciseMessage extends GameMessageBase {
  type: GameMessageType.GAME_EXERCISE;
  data: {
    gameName: string;
    registrationId: number;
    eduContentId: number;
    playTime: number; //float (seconds)
    question: string;
    answer: string; // (the answer the user selected/entered),
    correctAnswer: boolean;
  };
}

export interface GameWindowClosedMessage extends GameMessageBase {
  type: GameMessageType.GAME_WINDOW_CLOSED;
  data: {
    gameName: string;
    registrationId: number;
    eduContentId: number;
  };
}

export interface GameOpenInstructionsMessage extends GameMessageBase {
  type: GameMessageType.GAME_OPEN_INSTRUCTIONS;
  data: {
    eduContentId: number;
  };
}

export interface GameOpenUrlMessage extends GameMessageBase {
  type: GameMessageType.GAME_OPEN_URL;
  data: {
    url: string;
  };
}

export interface GameGetProfileDataMessage extends GameMessageBase {
  type: GameMessageType.GAME_GET_PROFILE_DATA;
  data: {
    keys: string[];
    methodCode: string;
  };
}

export interface GameProfileDataSaveMessage extends GameMessageBase {
  type: GameMessageType.GAME_SAVE_PROFILE_DATA;
  data: {
    profileData: UserGameProfileInterface; // The profile data that was saved
  };
}

export interface GameProfileDataSavedMessage extends GameMessageBase {
  type: GameMessageType.GAME_PROFILE_DATA_SAVED;
  data: {
    profileData: UserGameProfileInterface; // The profile data that was saved
  };
}

export interface GameProfileDataMessage extends GameMessageBase {
  type: GameMessageType.GAME_PROFILE_DATA;
  data: {
    profileData: Dictionary<UserGameProfileInterface>; // The profile data to be sent to the game
  };
}

export interface GameUserKwetonsMessage extends GameMessageBase {
  type: GameMessageType.GAME_USER_KWETONS;
  data: {
    kwetons: number;
  };
}

export interface GameUserPurchasedMessage extends GameMessageBase {
  type: GameMessageType.GAME_USER_PURCHASED;
  data: {
    kwetons: number; //new kwetons after purchase
  };
}

export interface GameUserPurchaseFailedMessage extends GameMessageBase {
  type: GameMessageType.GAME_USER_PURCHASE_FAILED;
  data: {
    error: string; // Error message explaining why the purchase failed
  };
}

export interface GameGetKwetonsMessage extends GameMessageBase {
  type: GameMessageType.GAME_GET_KWETONS;
}

export interface GamePurchaseMessage extends GameMessageBase {
  type: GameMessageType.GAME_PURCHASE;
  data: {
    price: number; // The price of the item being purchased
  };
}

export interface GameEarningMessage extends GameMessageBase {
  type: GameMessageType.GAME_EARNING;
  data: {
    amount: number; // The earned amount of kwetons
  };
}

export interface GameUserEarnedMessage extends GameMessageBase {
  type: GameMessageType.GAME_USER_EARNED;
  data: {
    kwetons: number; //new kwetons after earning
  };
}

export interface GameUserEarningFailedMessage extends GameMessageBase {
  type: GameMessageType.GAME_USER_EARNING_FAILED;
  data: {
    error: string; // Error message explaining why the earning failed
  };
}

export interface GameCompletedProcessedMessage extends GameMessageBase {
  type: GameMessageType.GAME_COMPLETED_PROCESSED;
  data: undefined;
}

export type GameMessage =
  | GameLoadedMessage
  | GameRegistrationMessage
  | GameCompletedMessage
  | GameRestartedMessage
  | GameExerciseMessage
  | GameOpenInstructionsMessage
  | GameOpenUrlMessage
  | GameWindowClosedMessage
  | GameGetProfileDataMessage
  | GameProfileDataMessage
  | GameProfileDataSavedMessage
  | GameProfileDataSaveMessage
  | GameUserKwetonsMessage
  | GameUserPurchasedMessage
  | GameUserPurchaseFailedMessage
  | GameGetKwetonsMessage
  | GamePurchaseMessage
  | GameEarningMessage
  | GameUserEarnedMessage
  | GameUserEarningFailedMessage
  | GameCompletedProcessedMessage;
